import { api } from "@/services/api";

export interface ReportFilter {
  startDate?: string;
  endDate?: string;
  clientId?: number;
  candidateName?: string;
}

export interface InvoiceReportResponse {
  invoiceNumber: string;
  invoiceDate: string;
  candidateName: string;
  services: string;
  amountPerService?: number;
  clinicName: string;
  serviceDate: string;
  totalAmount: number;
  vendorCost?: number;
  margin?: number;
}

export interface PricingMatrixResponse {
  services: string[];
  rows: {
    clinicId: number;
    clinicName: string;
    city: string;
    state: string;
    pricing: Record<string, {
      vendorPrice: number;
      margin: number;
      clientPrice: number;
    }>;
  }[];
}

export interface LocationUtilizationResponse {
  details: {
    vendorName: string;
    clinicName: string;
    city: string;
    state: string;
    totalVisits: number;
    completedCount: number;
    cancelledCount: number;
    noShowCount: number;
  }[];
}

export const reportsV2Api = api.injectEndpoints({
  endpoints: (builder) => ({
    getInvoiceReport: builder.query<InvoiceReportResponse[], ReportFilter | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params) {
          if (params.startDate) queryParams.append("startDate", params.startDate);
          if (params.endDate) queryParams.append("endDate", params.endDate);
          if (params.clientId) queryParams.append("clientId", params.clientId.toString());
          if (params.candidateName) queryParams.append("candidateName", params.candidateName);
        }
        return {
          url: `/reporting/invoices?${queryParams.toString()}`,
          method: "GET",
        };
      },
    }),
    getUtilizationReport: builder.query<LocationUtilizationResponse, ReportFilter | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params) {
          if (params.startDate) queryParams.append("startDate", params.startDate);
          if (params.endDate) queryParams.append("endDate", params.endDate);
        }
        return {
          url: `/reporting/utilization?${queryParams.toString()}`,
          method: "GET",
        };
      },
    }),
    getPricingMatrix: builder.query<PricingMatrixResponse, void>({
      query: () => ({
        url: "/reporting/pricing-matrix",
        method: "GET",
      }),
    }),
    getInvoiceReportCsv: builder.query<Blob, ReportFilter | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params) {
          if (params.startDate) queryParams.append("startDate", params.startDate);
          if (params.endDate) queryParams.append("endDate", params.endDate);
          if (params.clientId) queryParams.append("clientId", params.clientId.toString());
          if (params.candidateName) queryParams.append("candidateName", params.candidateName);
        }
        return {
          url: `/reporting/export/invoices/csv?${queryParams.toString()}`,
          method: "GET",
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),
    getInvoiceReportPdf: builder.query<Blob, ReportFilter | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params) {
          if (params.startDate) queryParams.append("startDate", params.startDate);
          if (params.endDate) queryParams.append("endDate", params.endDate);
          if (params.clientId) queryParams.append("clientId", params.clientId.toString());
          if (params.candidateName) queryParams.append("candidateName", params.candidateName);
        }
        return {
          url: `/reporting/export/invoices/pdf?${queryParams.toString()}`,
          method: "GET",
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),
    getUtilizationReportCsv: builder.query<Blob, ReportFilter | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params) {
          if (params.startDate) queryParams.append("startDate", params.startDate);
          if (params.endDate) queryParams.append("endDate", params.endDate);
        }
        return {
          url: `/reporting/export/utilization/csv?${queryParams.toString()}`,
          method: "GET",
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),
    getPricingMatrixCsv: builder.query<Blob, void>({
      query: () => ({
        url: "/reporting/export/pricing-matrix/csv",
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetInvoiceReportQuery,
  useGetUtilizationReportQuery,
  useGetPricingMatrixQuery,
  useLazyGetInvoiceReportCsvQuery,
  useLazyGetInvoiceReportPdfQuery,
  useLazyGetUtilizationReportCsvQuery,
  useLazyGetPricingMatrixCsvQuery,
} = reportsV2Api;
